import { useState } from "react";

import type { PerformanceReportBackLinkProps } from "../components/BackLink";
import type { PerfRow } from "../types";

import { InfoIcon } from "../../Icons";
import { SegmentControl } from "../../SegmentControl";
import { TableState } from "../../TableState";
import { Typo } from "../../Typo";
import { BackLink } from "../components/BackLink";
import { PerformanceFilterModal } from "../components/PerformanceFilterModal";
import { usePerformanceReportTranslations } from "../hooks/usePerformanceReportTranslations";
import { usePerformanceReportAnalyticsContext } from "../providers/PerformanceReportAnalyticsProvider";
import { usePerformanceReportContext } from "../providers/PerformanceReportProvider";
import {
  MobileHeaderSkeleton,
  MobileSortExportSkeleton,
  MobileTabsSkeleton,
  MobileToolbarSkeleton,
} from "./MobileChromeSkeleton";
import { MobileControls } from "./MobileControls";
import { MobileRowList } from "./MobileRowList";
import { MobileRowListHeader } from "./MobileRowListHeader";
import { MobileRowListSkeleton } from "./MobileRowListSkeleton";
import { MobileSortExportRow } from "./MobileSortExportRow";
import { MobileSummaryCards } from "./MobileSummaryCards";
import { PerformanceDetailScreen } from "./PerformanceDetailScreen";

/**
 * Mobile Performance report page (AC-1). Composes the mobile-only
 * subcomponents around the same shared `usePerformanceReportContext()` state
 * the desktop page uses — no separate data/state layer.
 *
 * Sheet/screen open state is split into what each sub-component needs: the
 * filter sheet is page-level state (it needs `initial`/`selectOptions` data
 * threaded in, matching desktop's `filterOpen` precedent for the same filter
 * modal); the date sheet is self-contained inside `MobileControls`, and the
 * sort sheet is self-contained inside `MobileSortExportRow` (neither exposes
 * a controlled `isOpen` prop). There is no separate columns sheet: the
 * updated AC (AC-2/AC-5, edited 2026-07 after the design-vs-AC conflict was
 * ruled on) confirms two controls only and one merged "View metrics" sheet.
 * Each sheet's own full-screen scrim makes "only one open at a
 * time" (AC-9) hold without a shared enum: opening a second sheet isn't
 * reachable while another is covering the screen.
 */
export type MobilePerformanceReportPageProps = {
  /** Back-navigation link to the originating Marketing tab. Omitted (e.g. by
   * backoffice) renders no back link. */
  backLink?: PerformanceReportBackLinkProps;
};

export const MobilePerformanceReportPage = ({
  backLink,
}: MobilePerformanceReportPageProps) => {
  const translations = usePerformanceReportTranslations();
  const analytics = usePerformanceReportAnalyticsContext();
  const {
    filters,
    setFilters,
    selectOptions,
    tab,
    setTab,
    tabOptions,
    rows,
    kpiCards,
    lastUpdated,
    isError,
    isLoading,
    isInitialLoading,
    isCardsLoading,
    isCardsError,
    isCardsEmpty,
    retryFetch,
    isOptionsLoading,
    onFilterOpen,
    page,
    pageSize,
    totalCount,
    setPage,
  } = usePerformanceReportContext();

  const [filterOpen, setFilterOpen] = useState(false);
  const [detailRow, setDetailRow] = useState<PerfRow | null>(null);

  return (
    <div className="grid grid-cols-1">
      {/* Back-navigation link (deep-linked from Marketing). URL-param-driven,
          not fetch-driven, so it renders outside the isInitialLoading branch
          below — a back affordance must not disappear during load. */}
      {backLink && (
        <div className="mb-3">
          <BackLink
            href={backLink.href}
            label={backLink.label}
            onClick={backLink.onClick}
            onNavigate={backLink.onNavigate}
          />
        </div>
      )}

      {/* On first entry the whole chrome is a skeleton; a later query change
          leaves it live and only reloads the stat cards + row list. */}
      {isInitialLoading ? (
        <MobileHeaderSkeleton />
      ) : (
        <div className="flex flex-col gap-3">
          <Typo element="h1" type="heading2XL" textColor="text-text-primary">
            {translations.headerTitle}
          </Typo>
          <div className="flex items-start gap-2">
            <InfoIcon
              className="h-4 w-4 shrink-0 text-icon-brand-default [&_path]:stroke-icon-brand-default"
              aria-hidden="true"
              focusable="false"
            />
            <Typo element="span" type="bodyXS" textColor="text-text-secondary">
              {translations.headerLastUpdated(lastUpdated)}
            </Typo>
          </div>
        </div>
      )}

      {/* 40px visual gap above (design spec): mt-8 (32px) + this bar's own
          py-2 top padding (8px) — the bar shares the page's bg-surface-primary,
          so its padding reads as part of the gap, not the margin alone. Pins
          directly beneath the app header (sticky top-0 z-20, ~64px tall),
          mirroring the ClientPage report convention: below the header at
          z-10 with an opaque backing so scrolled content passes behind it. */}
      <div className="sticky top-[64px] z-10 mt-8 bg-surface-primary py-2">
        {isInitialLoading ? (
          <MobileToolbarSkeleton />
        ) : (
          <MobileControls
            onOpenFilter={() => {
              analytics?.trackFilterButtonEvent?.();
              onFilterOpen();
              setFilterOpen(true);
            }}
          />
        )}
      </div>

      {/* 16px visual gap above: this bar's own py-2 bottom padding (8px) +
          mt-2 (8px) here — see note above on padding counting toward the gap. */}
      <div className="mt-2">
        <MobileSummaryCards
          cards={kpiCards}
          isLoading={isCardsLoading}
          isError={isCardsError}
          isEmpty={isCardsEmpty}
        />
      </div>

      <div className="mt-4">
        {isInitialLoading ? (
          <MobileTabsSkeleton />
        ) : (
          <SegmentControl
            options={tabOptions}
            value={tab}
            onChange={(next) => {
              analytics?.trackViewByEvent?.(next);
              setTab(next);
            }}
            aria-label={translations.viewBy}
            variant="filterButtons"
            scrollable
            fullWidth
          />
        )}
      </div>

      {/* Stacks directly beneath the pinned controls row: 64px header +
          56px controls block (py-2 + h-10 buttons, matching the canvas's
          40px control height) = 120px. Same z-10 + opaque-bg treatment as
          the ClientPage report sort row. 24px visual gap above: mt-4 (16px)
          + this bar's own py-2 top padding (8px), which reads as part of
          the gap since the bar shares the page's bg-surface-primary. */}
      <div className="sticky top-[calc(64px+56px)] z-10 mt-4 bg-surface-primary py-2">
        {isInitialLoading ? (
          <MobileSortExportSkeleton />
        ) : (
          <MobileSortExportRow />
        )}
      </div>

      {/* 24px visual gap above: this bar's own py-2 bottom padding (8px) +
          mt-4 (16px) here.

          `MobileRowListHeader` sits outside the state branch so the row list
          keeps its column header while loading, empty and errored — the AC's
          "the row list preserves its header", and how the design canvas keeps
          `.m-dhead` outside the same conditional. */}
      <div className="mt-4">
        <MobileRowListHeader />
        {isLoading ? (
          <MobileRowListSkeleton tab={tab} />
        ) : isError ? (
          <TableState
            variant="error"
            title={translations.errorTitle}
            description={translations.errorText}
            actionLabel={translations.errorActionText}
            onAction={retryFetch}
            className="min-h-[256px]"
          />
        ) : rows.length > 0 ? (
          <MobileRowList
            rows={rows}
            onRowTap={setDetailRow}
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
          />
        ) : (
          <TableState
            variant="empty"
            title={translations.emptyTitle}
            description={translations.emptyText}
            className="min-h-[256px]"
          />
        )}
      </div>

      <PerformanceFilterModal
        isOpen={filterOpen}
        initial={filters}
        selectOptions={selectOptions}
        isOptionsLoading={isOptionsLoading}
        onApply={(next) => {
          setFilters(next);
          setFilterOpen(false);
        }}
        onClose={() => setFilterOpen(false)}
      />

      <PerformanceDetailScreen
        row={detailRow}
        onClose={() => setDetailRow(null)}
      />
    </div>
  );
};
